import { defineComponent as _defineComponent } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import { ValidationObserver } from 'vee-validate'
import { computed, reactive, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router/composables'

import LInput from '@/components/core/form/Input.vue'
import LButton from '@/components/core/Button.vue'
import FormGroup from '@/components/core/form/FormGroup.vue'
import SingleSignOnSection from '@/components/onboarding/singleSignOn/SingleSignOnSection.vue'
import LegalNotice from '@/components/onboarding/LegalNotice.vue'
import {
  useAuth,
  useSetTokens,
  useSignUp,
  usePendingActions,
  useSignIn,
  useMigrateAnonymousUser,
} from '@/composables/auth'
import { useMcpAuth } from '@/composables/mcp-auth'
import { useContext } from '@/composables/context'
import { eventModule } from '@/store'
import { logEvent } from '@/utils/utility-manager'


export default /*#__PURE__*/_defineComponent({
  __name: 'SignUpForm',
  setup(__props) {

const auth = useAuth()
const router = useRouter()
const route = useRoute()
const { isMcpOAuthFlow, completeMcpAuth } = useMcpAuth()
const { i18n } = useContext()
const { setTokens } = useSetTokens()
const { mutate: signUp } = useSignUp()
const { mutate: signIn } = useSignIn()
const { handlePendingActions, getPendingActionsType } = usePendingActions()
const { mutate: migrateAnonymousUser } = useMigrateAnonymousUser()

const showPassword = ref(false)
const isAnonymous = computed(
  () => auth.isSignedIn && auth.accountType === 'anonymous',
)

const loginTitle = computed(() => {
  let title = i18n.t('signUp.title').toString()

  if (
    typeof route.query.newTrip === 'string' ||
    typeof route.query.assistant_sid === 'string' ||
    route.query.utm_source === 'onboarding'
  ) {
    title = i18n.t('signUp.titleActionNewTrip').toString()
  } else if (typeof route.query.inspirationId === 'string') {
    title = i18n.t('signUp.titleActionInspiration').toString()
  } else if (typeof route.query.pinCode === 'string') {
    title = i18n.t('signUp.titleActionJoinTrip').toString()
  }

  return title
})

const formData = reactive({
  email: '',
  password: '',
  tos: false,
})

const isLoading = ref(false)

function handleSignUpError(error: unknown) {
  if (!axios.isAxiosError(error)) {
    eventModule.newError('error.generic')
    return
  }

  const emailAlreadyExistsCodes = [12020204, 12020205, 12040106]
  const errorCode = error.response?.data.code ?? -1

  if (emailAlreadyExistsCodes.includes(errorCode)) {
    eventModule.newError('error.user.emailAlreadyExists')
  } else {
    eventModule.newError('error.user.create')
  }
}

function performSignIn() {
  signIn(
    {
      username: formData.email,
      password: formData.password,
    },
    {
      onSuccess: async (authResponse) => {
        logEvent('registration_success', {
          method: 'form',
          onboarding_events: getPendingActionsType(),
        })

        if (await completeMcpAuth(authResponse)) return

        setTokens(authResponse)
        const redirectPath = await handlePendingActions()
        await router.push({ path: redirectPath ?? '/' })
      },
      onError: () => {
        // Error is already handled by the useSignIn hook
        isLoading.value = false
      },
    },
  )
}

function handleFormSubmit() {
  isLoading.value = true

  if (isAnonymous.value) {
    migrateAnonymousUser(
      {
        email: formData.email,
        password: formData.password,
      },
      {
        onSuccess: () => {
          performSignIn()
        },
        onError: (error) => {
          handleSignUpError(error)
          isLoading.value = false
        },
      },
    )
  } else {
    const credentials = {
      ...formData,
      locale: i18n.locale,
      organizationId: Cookies.get('l_oid'),
    }

    signUp(credentials, {
      onSuccess: () => {
        performSignIn()
      },
      onError: (error) => {
        handleSignUpError(error)
        isLoading.value = false
      },
    })
  }
}

return { __sfc: true,auth, router, route, isMcpOAuthFlow, completeMcpAuth, i18n, setTokens, signUp, signIn, handlePendingActions, getPendingActionsType, migrateAnonymousUser, showPassword, isAnonymous, loginTitle, formData, isLoading, handleSignUpError, performSignIn, handleFormSubmit, ValidationObserver, LInput, LButton, FormGroup, SingleSignOnSection, LegalNotice }
}

})